/**
 * Estimación de 1RM, porcentajes de carga y volumen de entrenamiento.
 */
import { roundTo } from '../units.js';
/** Repeticiones máximas alcanzables en cada porcentaje del 1RM (tabla clásica). */
export const REP_PERCENT_TABLE = Object.freeze([
    [100, 1],
    [95, 2],
    [93, 3],
    [90, 4],
    [87, 5],
    [85, 6],
    [83, 7],
    [80, 8],
    [77, 9],
    [75, 10],
    [70, 12],
    [65, 15],
]);
function assertSet(weightKg, reps) {
    if (!Number.isFinite(weightKg) || weightKg < 0) {
        throw new RangeError('weightKg debe ser un número finito mayor o igual que 0');
    }
    if (!Number.isInteger(reps) || reps < 1) {
        throw new RangeError('reps debe ser un entero mayor o igual que 1');
    }
}
/** Epley (1985): `1RM = w · (1 + reps/30)`. Redondeo a 1 decimal. */
export function epley(weightKg, reps) {
    assertSet(weightKg, reps);
    if (reps === 1)
        return roundTo(weightKg, 1);
    return roundTo(weightKg * (1 + reps / 30), 1);
}
/**
 * Brzycki (1993): `1RM = w · 36 / (37 − reps)`. Sólo válida por debajo de
 * 37 repeticiones (la fórmula diverge y cambia de signo a partir de ahí).
 */
export function brzycki(weightKg, reps) {
    assertSet(weightKg, reps);
    if (reps >= 37) {
        throw new RangeError('brzycki no es aplicable con 37 repeticiones o más');
    }
    if (reps === 1)
        return roundTo(weightKg, 1);
    return roundTo((weightKg * 36) / (37 - reps), 1);
}
/** Estima el 1RM con la fórmula elegida (Epley por defecto). */
export function estimate1RM(weightKg, reps, formula = 'epley') {
    return formula === 'brzycki' ? brzycki(weightKg, reps) : epley(weightKg, reps);
}
/** Carga correspondiente a un porcentaje del 1RM. Redondeo a 1 decimal. */
export function percentOf1RM(oneRm, pct) {
    if (!Number.isFinite(oneRm) || oneRm < 0) {
        throw new RangeError('oneRm debe ser un número finito mayor o igual que 0');
    }
    if (!Number.isFinite(pct) || pct < 0) {
        throw new RangeError('pct debe ser un número finito mayor o igual que 0');
    }
    return roundTo((oneRm * pct) / 100, 1);
}
/**
 * Repeticiones aproximadas que se pueden completar a un porcentaje del 1RM.
 * Aproxima al valor más cercano de {@link REP_PERCENT_TABLE}; en caso de empate
 * elige el porcentaje mayor (la estimación más conservadora).
 */
export function repsAtPercent(pct) {
    if (!Number.isFinite(pct)) {
        throw new RangeError('pct debe ser un número finito');
    }
    let best = REP_PERCENT_TABLE[0];
    let bestDistance = Math.abs(best[0] - pct);
    for (const entry of REP_PERCENT_TABLE) {
        const distance = Math.abs(entry[0] - pct);
        if (distance < bestDistance) {
            best = entry;
            bestDistance = distance;
        }
    }
    return best[1];
}
/**
 * Volumen de una serie (tonelaje): `peso × repeticiones`. En un ejercicio
 * unilateral las repeticiones son por lado, así que cuenta el doble; es la
 * misma regla que aplica el servidor en el historial y el progreso.
 */
export function setVolume(weightKg, reps, unilateral = false) {
    assertSet(weightKg, reps);
    return roundTo(weightKg * reps * (unilateral ? 2 : 1), 2);
}
/**
 * Volumen total de una sesión o de un ejercicio. Con el mismo criterio que el
 * servidor: los calentamientos no cuentan y las series unilaterales, doble.
 */
export function sessionVolume(sets) {
    let total = 0;
    for (const set of sets) {
        if (set.setType === 'warmup')
            continue;
        total += setVolume(set.weightKg, set.reps, set.unilateral === true);
    }
    return roundTo(total, 2);
}
/**
 * Indica si una serie supone un récord personal frente al mejor 1RM estimado
 * anterior. `previousBest1RM` en 0 (o nulo) significa «sin marca previa».
 */
export function isPR(previousBest1RM, weightKg, reps, formula = 'epley') {
    const estimated = estimate1RM(weightKg, reps, formula);
    if (previousBest1RM === null || previousBest1RM === undefined)
        return estimated > 0;
    return estimated > previousBest1RM;
}
//# sourceMappingURL=oneRepMax.js.map